import React, { useCallback, useMemo } from "react";
import {
  Box,
  Image,
  Text,
  Flex,
  VStack,
  useBreakpointValue,
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import Performerr from "../images/Menu/Performerr.png";
import 吳曜宇 from "../images/Assets/吳曜宇.png";
import { TEXTS } from "../constants/texts";
import { useInView } from "react-intersection-observer";

interface CommonImageProps {
  onContextMenu: (e: { preventDefault: () => void }) => void;
  draggable: boolean;
}

const preventDefault = (e: { preventDefault: () => void }): void => {
  e.preventDefault();
};

const commonImageProps: CommonImageProps = {
  onContextMenu: preventDefault,
  draggable: false,
};

interface PerformerSectionProps {
  scrollDirection: "up" | "down";
}

const PerformerSection: React.FC<PerformerSectionProps> = ({
  scrollDirection,
}) => {
  // Intersection Observer設定
  const commonInViewConfig = useMemo(
    () => ({
      threshold: 0.3,
      triggerOnce: false,
    }),
    []
  );

  const { ref: performerRef, inView: performerInView } =
    useInView(commonInViewConfig);
  const { ref: performerTitleRef, inView: performerTitleInView } =
    useInView(commonInViewConfig);
  const { ref: performerImageRef, inView: performerImageInView } = useInView({
    ...commonInViewConfig,
    threshold: 0.5,
  });
  const { ref: performerTextRef, inView: performerTextInView } =
    useInView(commonInViewConfig);
  const { ref: performerOrchestraRef, inView: performerOrchestraInView } =
    useInView(commonInViewConfig);

  // バリアント定義
  const slideInVariants = useMemo(
    () => ({
      hidden: { opacity: 0, y: 20 },
      visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.8, ease: "easeOut" },
      },
      instant: { opacity: 1, y: 0, transition: { duration: 0 } },
    }),
    []
  );

  const slideInFromLeftVariants = useMemo(
    () => ({
      hidden: { opacity: 0, x: -50 },
      visible: {
        opacity: 1,
        x: 0,
        transition: { duration: 1.2, ease: "easeInOut" },
      },
      instant: { opacity: 1, x: 0, transition: { duration: 0 } },
    }),
    []
  );

  // 画面サイズに応じてアニメーションバリアントを切り替え
  const titleVariants =
    useBreakpointValue({
      base: slideInVariants,
      sm: slideInVariants,
      md: slideInFromLeftVariants,
    }) || slideInFromLeftVariants;

  // アニメーション状態決定のためのヘルパー関数
  const getAnimationState = useCallback(
    (isInView: boolean) => {
      if (!isInView) {
        // Avoid hiding when scrolling up to prevent flicker
        return scrollDirection === "down" ? "hidden" : "instant";
      }
      return scrollDirection === "up" ? "instant" : "visible";
    },
    [scrollDirection]
  );

  const commonImageSizes = {
    width: {
      base: "270px",
      sm: "320px",
      md: "270px",
      lg: "300px",
      xl: "370px",
      "2xl": "400px",
    },
    maxWidth: {
      base: "100%",
      md: "900px",
      lg: "1100px",
      xl: "1200px",
    },
  };

  return (
    <Box
      id="performer-section"
      pos="relative"
      w="100%"
      sx={{ willChange: "transform, opacity", backfaceVisibility: "hidden" }}
    >
      <Box
        ref={performerRef}
        width="100%"
        textAlign="center"
        mb={{
          base: "40px",
          sm: "50px",
          md: "60px",
          lg: "80px",
        }}
      >
        <motion.div
          initial="hidden"
          animate={getAnimationState(performerInView)}
          variants={slideInVariants}
          style={{ width: "100%" }}
        >
          <Image
            src={Performerr}
            alt="performer"
            w={{
              base: "90%",
              sm: "80%",
              md: "80%",
              lg: "80%",
              xl: "70%",
              "2xl": "65%",
            }}
            h="auto"
            maxW={{
              base: "360px",
              sm: "450px",
              md: "700px",
              lg: "900px",
              xl: "1200px",
              "2xl": "1200px",
            }}
            mx="auto"
            display="block"
            {...commonImageProps}
          />
        </motion.div>
      </Box>

      <Box
        width="100%"
        maxW={{
          base: "95%",
          sm: "90%",
          md: "80%",
          lg: "70%",
          xl: "70%",
          "2xl": "70%",
        }}
        mx="auto"
      >
        <VStack align="center" spacing={4} w="100%">
          <Box
            ref={performerTitleRef}
            width="100%"
            maxW={commonImageSizes.maxWidth}
            mx="auto"
          >
            <motion.div
              initial="hidden"
              animate={getAnimationState(performerTitleInView)}
              variants={titleVariants}
              style={{ width: "100%" }}
            >
              <Flex
                direction="column"
                align={{
                  base: "center",
                  md: "flex-start",
                }}
                maxW={{
                  base: "270px",
                  sm: "320px",
                  md: "400px",
                  lg: "400px",
                }}
                mx={{
                  base: "auto",
                  sm: "auto",
                  md: "0",
                }}
                width="100%"
              >
                <Text
                  color="white"
                  fontSize={{
                    base: "12px",
                    sm: "14px",
                    md: "18px",
                    lg: "20px",
                    xl: "25px",
                    "2xl": "25px",
                  }}
                  fontWeight="bold"
                  px={7}
                  py={1}
                  borderRadius="3xl"
                  w="fit-content"
                  bg="rgb(60, 17, 0)"
                  mb={{
                    base: 2,
                    sm: 2,
                    md: 4,
                    lg: 4,
                    xl: 4,
                    "2xl": 4,
                  }}
                  textAlign={{
                    base: "center",
                    sm: "center",
                    md: "left",
                  }}
                >
                  {TEXTS.Performer.content[0]}
                </Text>

                <Flex
                  align="center"
                  gap={4}
                  justify={{
                    base: "center",
                    sm: "center",
                    md: "flex-start",
                  }}
                  width="100%"
                  flexWrap="wrap"
                >
                  <Text
                    color="white"
                    fontSize={{
                      base: "18px",
                      sm: "21px",
                      md: "28px",
                      lg: "30px",
                      xl: "40px",
                      "2xl": "40px",
                    }}
                    fontWeight="bold"
                  >
                    {TEXTS.Performer.content[1]}
                  </Text>
                  <Text
                    color="white"
                    fontSize={{
                      base: "12px",
                      sm: "14px",
                      md: "18px",
                      lg: "20px",
                      xl: "25px",
                      "2xl": "25px",
                    }}
                  >
                    {TEXTS.Performer.content[2]}
                  </Text>
                </Flex>
              </Flex>
            </motion.div>
          </Box>

          <Flex
            gap={{
              base: 8,
              sm: 8,
              md: 0,
              lg: 10,
              xl: 10,
              "2xl": 0,
            }}
            align="flex-start"
            direction={{
              base: "column",
              sm: "column",
              md: "row",
            }}
            alignItems={{
              base: "center",
              sm: "center",
              md: "flex-start",
            }}
            justifyContent="center"
            mt={{
              base: 4,
              sm: 6,
              md: 8,
              lg: 10,
            }}
            width="100%"
            maxW={commonImageSizes.maxWidth}
            mx="auto"
            px={{
              base: 0,
              sm: 0,
              md: 0,
            }}
          >
            <Box
              ref={performerImageRef}
              width={{
                base: "100%",
                md: "300px", // md時は画像サイズに近い固定幅
                lg: "40%",
              }}
              flexShrink={0} // Flexで縮小されないよう固定
              sx={{
                "@media (min-width: 48em) and (max-width: 61.9em)": {
                  marginRight: "40px !important",
                },
                "@media (min-width: 1000px) and (max-width: 1100px)": {
                  marginRight: "40px !important", // 1024px付近のマージンを適度に拡大
                },
              }}
            >
              <motion.div
                initial={{ opacity: 0 }}
                animate={
                  performerImageInView
                    ? scrollDirection === "up"
                      ? { opacity: 1, transition: { duration: 0 } }
                      : {
                          opacity: 1,
                          transition: { duration: 1, ease: "easeOut" },
                        }
                    : scrollDirection === "up"
                    ? { opacity: 1, transition: { duration: 0 } }
                    : { opacity: 0, transition: { duration: 0 } }
                }
                style={{ width: "100%" }}
              >
                <Box
                  maxW={commonImageSizes.width}
                  mx={{
                    base: "auto",
                    sm: "auto",
                    md: "0",
                  }}
                  position="relative"
                >
                  <Box
                    width={commonImageSizes.width}
                    height="auto"
                    overflow="hidden"
                    position="relative"
                    sx={{
                      "&::before": {
                        content: '""',
                        display: "block",
                        paddingTop: "66.6667%",
                      },
                    }}
                  >
                    <motion.div
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        overflow: "hidden",
                      }}
                      initial={{ opacity: 0 }}
                      animate={
                        performerImageInView
                          ? scrollDirection === "up"
                            ? { opacity: 1, transition: { duration: 0 } }
                            : {
                                opacity: 1,
                                transition: { duration: 1, ease: "easeOut" },
                              }
                          : scrollDirection === "up"
                          ? { opacity: 1, transition: { duration: 0 } }
                          : { opacity: 0, transition: { duration: 0 } }
                      }
                    >
                      <motion.div
                        initial={{ scale: 1.2 }}
                        animate={
                          performerImageInView
                            ? scrollDirection === "up"
                              ? { scale: 1, transition: { duration: 0 } }
                              : {
                                  scale: 1,
                                  transition: {
                                    duration: 1,
                                    ease: "easeInOut",
                                  },
                                }
                            : scrollDirection === "up"
                            ? { scale: 1, transition: { duration: 0 } }
                            : { scale: 1.2, transition: { duration: 0 } }
                        }
                        style={{
                          width: "100%",
                          height: "100%",
                          transformOrigin: "center",
                        }}
                      >
                        <Image
                          src={吳曜宇}
                          alt="吳曜宇"
                          width="100%"
                          height="auto"
                          loading="eager"
                          decoding="async"
                          {...commonImageProps}
                        />
                      </motion.div>
                    </motion.div>
                  </Box>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={
                      performerImageInView
                        ? scrollDirection === "up"
                          ? { opacity: 1, y: 0, transition: { duration: 0 } }
                          : {
                              opacity: 1,
                              y: 0,
                              transition: {
                                duration: 0.5,
                                ease: "easeOut",
                                delay: 0.3,
                              },
                            }
                        : { opacity: 0, y: 20, transition: { duration: 0 } }
                    }
                  >
                    <Flex
                      justify="flex-end"
                      align="center"
                      gap={1}
                      color="black"
                      fontSize={{
                        base: "10px",
                        sm: "10px",
                        md: "10px",
                        lg: "14px",
                        xl: "14px",
                      }}
                      width="100%"
                      position="relative"
                      mt={{ base: 0, sm: 0, md: 0 }}
                      textAlign="right"
                    >
                      <Text>{TEXTS.Performer.content[8]}</Text>
                      <Text>{TEXTS.Performer.content[9]}</Text>
                    </Flex>
                  </motion.div>
                </Box>
              </motion.div>
            </Box>

            <Box
              ref={performerTextRef}
              width={{
                base: "100%",
                md: "auto", // md時は残りスペースを使用
                lg: "60%",
                xl: "60%",
                "2xl": "60%",
              }}
              flex={{
                md: 1, // md時は残りスペースを全て使用
                lg: "unset",
              }}
              minWidth={0} // Flexのはみ出し防止
            >
              <motion.div
                initial="hidden"
                animate={getAnimationState(performerTextInView)}
                variants={slideInVariants}
                style={{ width: "100%" }}
              >
                <VStack
                  spacing={0}
                  align={{
                    base: "center",
                    md: "flex-start",
                  }}
                  width="100%"
                >
                  {TEXTS.Performer.content.slice(3, 6).map((text, index) => (
                    <Text
                      key={`performer-description-${index}`}
                      color="white"
                      fontSize={{
                        base: "11px",
                        sm: "11px",
                        md: "13px",
                        lg: "13px",
                        xl: "18px",
                        "2xl": "20px",
                      }}
                      lineHeight="1.5"
                      textAlign="left"
                      whiteSpace="pre-line"
                      width="100%"
                      px={{
                        base: "4",
                        sm: "4",
                        md: "0",
                      }}
                    >
                      {text}
                    </Text>
                  ))}
                </VStack>
              </motion.div>
            </Box>
          </Flex>

          <Box
            ref={performerOrchestraRef}
            width="100%"
            maxW={commonImageSizes.maxWidth}
            mx="auto"
            mt={{
              base: 8,
              sm: 10,
              md: 12,
              lg: 16,
            }}
            mb={{
              base: 10,
              sm: 12,
              md: 16,
              lg: 20,
            }}
          >
            <motion.div
              initial="hidden"
              animate={getAnimationState(performerOrchestraInView)}
              variants={{
                hidden: {
                  opacity: 0,
                  x: useBreakpointValue({ base: 0, md: -50 }),
                  y: useBreakpointValue({ base: 20, md: 0 }),
                  transition: { duration: 0 },
                },
                visible: {
                  opacity: 1,
                  x: 0,
                  y: 0,
                  transition: { duration: 1.2, ease: "easeInOut" },
                },
                instant: {
                  opacity: 1,
                  x: 0,
                  y: 0,
                  transition: { duration: 0 },
                },
              }}
              style={{ width: "100%" }}
            >
              <Flex
                direction="column"
                align={{
                  base: "center",
                  md: "flex-start",
                }}
                maxW={{
                  base: "270px",
                  sm: "320px",
                  md: "400px",
                  lg: "400px",
                }}
                mx={{
                  base: "auto",
                  sm: "auto",
                  md: "0",
                }}
              >
                <Text
                  color="white"
                  fontSize={{
                    base: "12px",
                    sm: "14px",
                    md: "18px",
                    lg: "20px",
                    xl: "25px",
                    "2xl": "25px",
                  }}
                  fontWeight="bold"
                  px={7}
                  py={1}
                  borderRadius="3xl"
                  w="fit-content"
                  bg="rgb(60, 17, 0)"
                  textAlign={{
                    base: "center",
                    md: "left",
                  }}
                >
                  {TEXTS.Performer.content[6]}
                </Text>
                <Text
                  color="white"
                  fontWeight="bold"
                  fontSize={{
                    base: "18px",
                    sm: "21px",
                    md: "24px",
                    lg: "30px",
                    xl: "40px",
                    "2xl": "40px",
                  }}
                  textAlign={{
                    base: "center",
                    md: "left",
                  }}
                  whiteSpace={{
                    base: "normal",
                    md: "nowrap",
                  }}
                  mt={{
                    base: 2,
                    sm: 3,
                    md: 4,
                    lg: 5,
                  }}
                >
                  {TEXTS.Performer.content[7]}
                </Text>
              </Flex>
            </motion.div>
          </Box>
        </VStack>
      </Box>
    </Box>
  );
};

export default PerformerSection;
